import { useEffect, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { ArrowDown, ArrowUp, ChevronRight, Edit2, GitBranch, Plus, Star, Trash2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { Modal } from '@/components/Modal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { LoadErrorState } from '@/components/ListStates';
import { SettingsCard, SettingsPage, SettingsSection } from '@/components/settings/SettingsPage';
import { useAuth } from '@/hooks/useAuth';
import { dealsService, Pipeline, PipelineStage } from '@/services/deals.service';

const TERMINAL_LABELS: Record<PipelineStage['terminal'], string> = {
  active: 'Active',
  won: 'Won',
  lost: 'Lost',
};

const TERMINAL_COLORS: Record<PipelineStage['terminal'], string> = {
  active: 'bg-info-soft text-info',
  won: 'bg-success-soft text-success',
  lost: 'bg-danger-soft text-danger',
};

const ICON_BTN = 'flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors';
const REVEAL = 'md:opacity-0 md:transition-opacity md:group-hover:opacity-100 md:group-focus-within:opacity-100';

export default function PipelinesSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin';
  const [pipelines, setPipelines] = useState<Pipeline[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [stagesByPipeline, setStagesByPipeline] = useState<Record<string, PipelineStage[]>>({});

  const [pipeOpen, setPipeOpen] = useState(false);
  const [pipeEditing, setPipeEditing] = useState<Pipeline | null>(null);
  const [pipeName, setPipeName] = useState('');
  const [pipeDescription, setPipeDescription] = useState('');
  const [pipeBusy, setPipeBusy] = useState(false);
  const [delPipe, setDelPipe] = useState<Pipeline | null>(null);

  const [stageOpen, setStageOpen] = useState(false);
  const [stageEditing, setStageEditing] = useState<PipelineStage | null>(null);
  const [stagePipelineUid, setStagePipelineUid] = useState<string>('');
  const [stageForm, setStageForm] = useState({
    name: '', order: 0, terminal: 'active' as PipelineStage['terminal'],
    probability: 50, rotDays: '', color: '#3b82f6',
  });
  const [stageBusy, setStageBusy] = useState(false);
  const [delStage, setDelStage] = useState<PipelineStage | null>(null);

  const refresh = async () => {
    setLoadError(false);
    try {
      const ps = await dealsService.listPipelines();
      setPipelines(ps);
      const next: Record<string, PipelineStage[]> = { ...stagesByPipeline };
      await Promise.all(ps.map(async (p) => {
        if (expanded[p.pipelineUid] || !next[p.pipelineUid]) {
          next[p.pipelineUid] = await dealsService.listStages(p.pipelineUid).catch(() => []);
        }
      }));
      setStagesByPipeline(next);
    } catch {
      setLoadError(true);
    }
  };

  useEffect(() => {
    void refresh();
    // Once on mount: refresh() reads the expanded/stage state of whichever render calls it later.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleExpand = async (uid: string) => {
    const willExpand = !expanded[uid];
    setExpanded((s) => ({ ...s, [uid]: willExpand }));
    if (willExpand && !stagesByPipeline[uid]) {
      const stages = await dealsService.listStages(uid).catch(() => []);
      setStagesByPipeline((s) => ({ ...s, [uid]: stages }));
    }
  };

  const startPipeCreate = () => { setPipeEditing(null); setPipeName(''); setPipeDescription(''); setPipeOpen(true); };
  const startPipeEdit = (p: Pipeline) => { setPipeEditing(p); setPipeName(p.name); setPipeDescription(p.description ?? ''); setPipeOpen(true); };

  const submitPipeline = async () => {
    if (!pipeName.trim()) { toast.error('Name is required'); return; }
    setPipeBusy(true);
    try {
      if (pipeEditing) {
        await dealsService.updatePipeline(pipeEditing.pipelineUid, { name: pipeName.trim(), description: pipeDescription.trim() || undefined });
        toast.success('Pipeline updated');
      } else {
        await dealsService.createPipeline({ name: pipeName.trim(), description: pipeDescription.trim() || undefined });
        toast.success('Pipeline created');
      }
      setPipeOpen(false);
      await refresh();
    } catch {
      // axios interceptor already toasted backend message
    } finally { setPipeBusy(false); }
  };

  const removePipeline = async () => {
    if (!delPipe) return;
    try {
      await dealsService.deletePipeline(delPipe.pipelineUid);
      toast.success('Pipeline removed');
      setDelPipe(null);
      await refresh();
    } catch {
      // axios interceptor already toasted backend message
    }
  };

  const startStageCreate = (pipelineUid: string) => {
    setStageEditing(null);
    setStagePipelineUid(pipelineUid);
    const existing = stagesByPipeline[pipelineUid] ?? [];
    setStageForm({
      name: '', order: existing.length,
      terminal: 'active', probability: 50, rotDays: '', color: '#3b82f6',
    });
    setStageOpen(true);
  };

  const startStageEdit = (s: PipelineStage) => {
    setStageEditing(s);
    setStagePipelineUid(s.pipelineUid);
    setStageForm({
      name: s.name,
      order: s.order,
      terminal: s.terminal,
      probability: s.probability ?? 50,
      rotDays: s.rotDays ? String(s.rotDays) : '',
      color: s.color ?? '#3b82f6',
    });
    setStageOpen(true);
  };

  const submitStage = async () => {
    if (!stageForm.name.trim()) { toast.error('Stage name required'); return; }
    setStageBusy(true);
    try {
      const payload = {
        name: stageForm.name.trim(),
        order: stageForm.order,
        terminal: stageForm.terminal,
        probability: stageForm.probability,
        rotDays: stageForm.rotDays ? Number(stageForm.rotDays) : undefined,
        color: stageForm.color,
      };
      if (stageEditing) {
        await dealsService.updateStage(stageEditing.stageUid, payload);
        toast.success('Stage updated');
      } else {
        await dealsService.createStage(stagePipelineUid, payload);
        toast.success('Stage created');
      }
      setStageOpen(false);
      await refresh();
    } catch {
      // axios interceptor already toasted backend message
    } finally { setStageBusy(false); }
  };

  const removeStage = async () => {
    if (!delStage) return;
    try {
      await dealsService.deleteStage(delStage.stageUid);
      toast.success('Stage removed');
      setDelStage(null);
      await refresh();
    } catch {
      // axios interceptor already toasted backend message
    }
  };

  const moveStage = async (stage: PipelineStage, direction: -1 | 1) => {
    const list = stagesByPipeline[stage.pipelineUid] ?? [];
    const sorted = [...list].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((s) => s.stageUid === stage.stageUid);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    try {
      await dealsService.updateStage(stage.stageUid, { order: sorted[swapIdx].order });
      await dealsService.updateStage(sorted[swapIdx].stageUid, { order: stage.order });
      await refresh();
    } catch (e) {
      if (!(e as { response?: unknown })?.response) toast.error('Failed to reorder');
    }
  };

  return (
    <>
      <SettingsPage
        eyebrow="Workspace"
        title="Pipelines"
        description="The stages a deal moves through, from first contact to won or lost. Use a separate pipeline for each way you sell."
        actions={isAdmin && <Button iconLeft={<Plus size={14} />} onClick={startPipeCreate}>New pipeline</Button>}
      >
        <SettingsSection
          title="Your pipelines"
          description={isAdmin
            ? 'Open a pipeline to see and change its stages. The default pipeline can’t be deleted; deals from a deleted pipeline move to it.'
            : 'You have view-only access. Ask a company admin to make changes.'}
        >
          <SettingsCard padded={false}>
            {pipelines === null && loadError ? (
              <LoadErrorState bare what="your pipelines" onRetry={() => void refresh()} />
            ) : pipelines === null ? (
              <div className="space-y-2 p-5">{[0, 1].map((i) => <div key={i} className="skeleton h-14 rounded-xl" />)}</div>
            ) : pipelines.length === 0 ? (
              <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white shadow-soft"><GitBranch size={20} /></div>
                <div className="text-sm font-medium text-text-primary">No pipelines yet</div>
                <p className="max-w-sm text-xs text-text-muted">The default “Sales pipeline” is created automatically when you add your first deal.</p>
                {isAdmin && <Button size="sm" variant="secondary" iconLeft={<Plus size={13} />} onClick={startPipeCreate}>Create a pipeline</Button>}
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {pipelines.map((p) => {
                  const isExpanded = !!expanded[p.pipelineUid];
                  const loaded = stagesByPipeline[p.pipelineUid];
                  const stages = (loaded ?? []).slice().sort((a, b) => a.order - b.order);
                  return (
                    <li key={p.pipelineUid}>
                      <div className="group flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-surface-alt/60">
                        <button
                          type="button"
                          onClick={() => toggleExpand(p.pipelineUid)}
                          aria-expanded={isExpanded}
                          className="flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-soft @md:gap-4"
                        >
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gold-gradient-soft text-primary"><GitBranch size={17} /></span>
                          <span className="min-w-0 flex-1">
                            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                              <span className="truncate text-sm font-semibold text-text-primary">{p.name}</span>
                              {p.isDefault && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-primary-soft px-2 py-0.5 text-2xs font-semibold text-primary">
                                  <Star size={10} fill="currentColor" /> Default
                                </span>
                              )}
                              {loaded && (
                                <span className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs text-text-muted">
                                  {stages.length} stage{stages.length === 1 ? '' : 's'}
                                </span>
                              )}
                            </span>
                            {p.description && <span className="mt-0.5 block truncate text-xs text-text-muted">{p.description}</span>}
                          </span>
                          <ChevronRight size={16} className={clsx('shrink-0 text-text-muted transition-transform', isExpanded && 'rotate-90')} aria-hidden />
                        </button>
                        {isAdmin && (
                          <div className={clsx('flex shrink-0 items-center gap-0.5', REVEAL)}>
                            <button type="button" onClick={() => startPipeEdit(p)} className={clsx(ICON_BTN, 'hover:bg-surface-alt hover:text-text-primary')} aria-label={`Edit ${p.name}`} title="Edit pipeline">
                              <Edit2 size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDelPipe(p)}
                              disabled={p.isDefault}
                              className={clsx(ICON_BTN, p.isDefault ? 'cursor-not-allowed opacity-40' : 'hover:bg-danger-soft hover:text-danger')}
                              aria-label={`Delete ${p.name}`}
                              title={p.isDefault ? 'The default pipeline can’t be deleted' : 'Delete pipeline'}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>

                      {isExpanded && (
                        <div className="border-t border-border bg-surface-alt/40 px-5 py-4">
                          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                            <span className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">Stages</span>
                            {isAdmin && (
                              <Button size="sm" variant="secondary" iconLeft={<Plus size={13} />} onClick={() => startStageCreate(p.pipelineUid)}>
                                Add stage
                              </Button>
                            )}
                          </div>

                          {!loaded ? (
                            <div className="skeleton h-12 rounded-xl" />
                          ) : stages.length === 0 ? (
                            <p className="rounded-xl border border-dashed border-border px-4 py-5 text-center text-xs text-text-muted">No stages yet.</p>
                          ) : (
                            <ol className="space-y-1.5">
                              {stages.map((s, idx) => (
                                <li key={s.stageUid} className="group flex items-center gap-3 rounded-xl border border-border bg-surface px-3.5 py-2.5">
                                  <span className="h-3 w-3 shrink-0 rounded-full border border-black/10" style={{ background: s.color ?? '#3b82f6' }} aria-hidden />
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                      <span className="truncate text-sm font-medium text-text-primary">{s.name}</span>
                                      <span className={clsx('rounded-full px-2 py-0.5 text-2xs font-semibold', TERMINAL_COLORS[s.terminal])}>
                                        {TERMINAL_LABELS[s.terminal]}
                                      </span>
                                      <span className="text-2xs tabular-nums text-text-muted">{s.probability}%</span>
                                    </div>
                                    {s.rotDays ? (
                                      <div className="mt-0.5 text-2xs text-text-muted">Flagged as stuck after {s.rotDays} day{s.rotDays === 1 ? '' : 's'}</div>
                                    ) : null}
                                  </div>
                                  {isAdmin && (
                                    <div className={clsx('flex shrink-0 items-center gap-0.5', REVEAL)}>
                                      <button type="button" onClick={() => moveStage(s, -1)} disabled={idx === 0} className={clsx(ICON_BTN, 'hover:bg-surface-alt hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent')} aria-label={`Move ${s.name} up`} title="Move up">
                                        <ArrowUp size={14} />
                                      </button>
                                      <button type="button" onClick={() => moveStage(s, 1)} disabled={idx === stages.length - 1} className={clsx(ICON_BTN, 'hover:bg-surface-alt hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent')} aria-label={`Move ${s.name} down`} title="Move down">
                                        <ArrowDown size={14} />
                                      </button>
                                      <button type="button" onClick={() => startStageEdit(s)} className={clsx(ICON_BTN, 'hover:bg-surface-alt hover:text-text-primary')} aria-label={`Edit ${s.name}`} title="Edit stage">
                                        <Edit2 size={14} />
                                      </button>
                                      <button type="button" onClick={() => setDelStage(s)} className={clsx(ICON_BTN, 'hover:bg-danger-soft hover:text-danger')} aria-label={`Delete ${s.name}`} title="Delete stage">
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  )}
                                </li>
                              ))}
                            </ol>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </SettingsCard>
        </SettingsSection>
      </SettingsPage>

      {/* Outside SettingsPage: its container box would become the containing block of these fixed overlays. They sit in no container, so their grids use viewport breakpoints. */}
      <Modal open={pipeOpen} onClose={() => !pipeBusy && setPipeOpen(false)} title={pipeEditing ? `Edit "${pipeEditing.name}"` : 'New pipeline'}>
        <FormField label="Pipeline name" required>
          <Input value={pipeName} onChange={(e) => setPipeName(e.target.value)} placeholder="Enterprise sales" />
        </FormField>
        <FormField label="Description">
          <Input value={pipeDescription} onChange={(e) => setPipeDescription(e.target.value)} placeholder="Optional context" />
        </FormField>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setPipeOpen(false)} disabled={pipeBusy}>Cancel</Button>
          <Button onClick={submitPipeline} loading={pipeBusy}>{pipeEditing ? 'Save' : 'Create'}</Button>
        </div>
      </Modal>

      <Modal open={stageOpen} onClose={() => !stageBusy && setStageOpen(false)} title={stageEditing ? `Edit "${stageEditing.name}"` : 'New stage'}>
        <FormField label="Stage name" required>
          <Input value={stageForm.name} onChange={(e) => setStageForm({ ...stageForm, name: e.target.value })} placeholder="Qualified" />
        </FormField>
        <div className="grid gap-x-4 sm:grid-cols-2">
          <FormField label="Type" hintBelow hint="Won and lost stages close the deal.">
            <Select
              value={stageForm.terminal}
              onChange={(e) => { const terminal = e.target.value as PipelineStage['terminal']; setStageForm({ ...stageForm, terminal, probability: terminal === 'won' ? 100 : terminal === 'lost' ? 0 : stageForm.probability }); }}
            >
              <option value="active">Active</option>
              <option value="won">Won (closes deal)</option>
              <option value="lost">Lost (closes deal)</option>
            </Select>
          </FormField>
          <FormField label="Chance of winning (%)" hintBelow hint={stageForm.terminal === 'active' ? '0–100, used for forecasting.' : 'Won is 100%; Lost is 0%. Move existing deals before changing a stage outcome.'}>
            <Input
              type="number"
              min={0}
              max={100}
              disabled={stageForm.terminal !== 'active'}
              value={stageForm.probability}
              onChange={(e) => setStageForm({ ...stageForm, probability: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
            />
          </FormField>
        </div>
        <div className="grid gap-x-4 sm:grid-cols-2">
          <FormField label="Flag as stuck after (days)" hintBelow hint="Deals that sit in this stage this long get flagged.">
            <Input
              type="number"
              min={1}
              value={stageForm.rotDays}
              onChange={(e) => setStageForm({ ...stageForm, rotDays: e.target.value })}
              placeholder="e.g. 14"
            />
          </FormField>
          <FormField label="Colour">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={stageForm.color}
                onChange={(e) => setStageForm({ ...stageForm, color: e.target.value })}
                aria-label="Pick a colour"
                className="h-10 w-12 shrink-0 cursor-pointer rounded-lg border border-border bg-surface p-1"
              />
              <div className="min-w-0 flex-1">
                <Input
                  value={stageForm.color}
                  onChange={(e) => setStageForm({ ...stageForm, color: e.target.value })}
                  placeholder="#3b82f6"
                />
              </div>
            </div>
          </FormField>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setStageOpen(false)} disabled={stageBusy}>Cancel</Button>
          <Button onClick={submitStage} loading={stageBusy}>{stageEditing ? 'Save' : 'Create'}</Button>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!delPipe}
        title="Delete pipeline?"
        message={delPipe ? `Delete "${delPipe.name}" and all its stages? Existing deals will be moved to your default pipeline.` : ''}
        danger
        onConfirm={removePipeline}
        onCancel={() => setDelPipe(null)}
      />
      <ConfirmDialog
        open={!!delStage}
        title="Delete stage?"
        message={delStage ? `Delete "${delStage.name}"? Deals in this stage will be moved to the next active stage.` : ''}
        danger
        onConfirm={removeStage}
        onCancel={() => setDelStage(null)}
      />
    </>
  );
}
